"use client";

import React from "react";
import { CalendarDays, ScrollText, Users } from "lucide-react";
import type { IHouseRules } from "../services";
import InfoSheet from "./InfoSheet";
import ThingsToKnowTile from "./ThingsToKnowTile";

interface IHouseRulesProps {
  rules: IHouseRules;
}

const Line = ({ icon, text }: { icon: React.ReactNode; text: string }) => (
  <li className="flex items-start gap-4 py-2.5">
    <span className="mt-0.5 flex-shrink-0 text-ink">{icon}</span>
    <span className="min-w-0 break-words text-base text-ink-black">{text}</span>
  </li>
);

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <section className="border-t border-neutral-200 py-6">
    <h4 className="mb-2 text-lg font-semibold text-ink-black">{title}</h4>
    {children}
  </section>
);

const HouseRulesModal = ({
  open,
  onClose,
  rules,
}: {
  open: boolean;
  onClose: () => void;
  rules: IHouseRules;
}) => (
  <InfoSheet open={open} onClose={onClose} title="House rules">
    <p className="mt-4 mb-6 text-base text-ink-gray">
      You&apos;ll be staying in someone&apos;s home, so please treat it with
      care and respect.
    </p>

    {rules.checkInOut.length > 0 && (
      <Section title="Checking in and out">
        <ul>
          {rules.checkInOut.map((line) => (
            <Line
              key={line}
              icon={<CalendarDays className="h-6 w-6" />}
              text={line}
            />
          ))}
        </ul>
      </Section>
    )}

    {(rules.maxGuests || rules.rules.length > 0 || rules.additional) && (
      <Section title="During your stay">
        <ul>
          {rules.maxGuests && (
            <Line icon={<Users className="h-6 w-6" />} text={rules.maxGuests} />
          )}
          {rules.rules.map((rule, idx) => (
            <Line
              key={idx}
              icon={<ScrollText className="h-6 w-6" />}
              text={rule}
            />
          ))}
        </ul>
        {rules.additional && (
          <div className="mt-4">
            <p className="mb-1 font-medium text-ink-black">Additional rules</p>
            <p className="whitespace-pre-line break-words text-base text-ink-dark">
              {rules.additional}
            </p>
          </div>
        )}
      </Section>
    )}
  </InfoSheet>
);

const HouseRules: React.FC<IHouseRulesProps> = ({ rules }) => {
  const [open, setOpen] = React.useState(false);
  const summary = [
    ...rules.checkInOut,
    ...(rules.maxGuests ? [rules.maxGuests] : []),
  ];
  return (
    <>
      <ThingsToKnowTile
        iconSrc="/icons/listing/listing_things_house_rules.svg"
        title="House rules"
        onOpen={() => setOpen(true)}
      >
        {summary.map((line) => (
          <span key={line} className="block">
            {line}
          </span>
        ))}
      </ThingsToKnowTile>
      <HouseRulesModal
        open={open}
        onClose={() => setOpen(false)}
        rules={rules}
      />
    </>
  );
};

export default HouseRules;
